import type { FormikErrors } from "formik";
import type { Entrega } from "../../carga/utils/agruparEntregas";
import type CargaItem from "../../carga/types/carga_item";
import type GestaoEntrega from "../types/gestao_entrega";
import type { RespostaQuestionarioSatisfacaoItem } from "../types/questionario_satisfacao";
import type { RegistroEntregaRegrasEfetivas } from "../../../utils/registroEntregaRegras";
import { nomeRecebedorTemNomeESobrenome } from "./nomeRecebedor";
import { erroDoMotivoDevolucao, motivoDevolucaoEmTexto, temMotivoDevolucaoEmTexto } from "./motivoDevolucao";

export type NotaEntregaFormValues = {
  j10_ukey: string;
  nota_numero: string;
  e_nota_pallet: string;
  recusou: boolean;
  url_imagem1: string;
  url_imagem2: string;
  motivo_devolucao: string;
  motivo_devolucao_descricao: string;
  e_tentativa_entrega: number;
  status_nota_pallet_gestao_entrega: number;
  motivo_nao_retorno_do_vale_pallet: string;
  url_imagem_vale_pallet: string;
};

export type EntregaGrupoFormValues = {
  notas: NotaEntregaFormValues[];
  nome_recebedor: string;
  url_assinatura_recebedor: string;
  observacoes: string;
  teve_devolucao_mercadoria: number | null;
  url_imagem_devolucao_mercadoria: string;
};

export type OpcoesValidacaoEntregaGrupo = {
  devolucaoExigirApenasFoto2: boolean;
  modalJaAberto: boolean;
  /** Empresa que digita o motivo da devolução em vez de escolher na lista. */
  motivoTextoLivre?: boolean;
};

export type ExtrasEntregaGrupo = {
  latitude: number;
  longitude: number;
  respostas_questionario: RespostaQuestionarioSatisfacaoItem[];
};

export const eNotaPallet = (nota: { e_nota_pallet: string }) => nota.e_nota_pallet == "1";
export const notaEntregue = (nota: NotaEntregaFormValues) => !nota.recusou;
export const notaEntregueNaoPallet = (nota: NotaEntregaFormValues) => notaEntregue(nota) && !eNotaPallet(nota);
export const notaEntreguePallet = (nota: NotaEntregaFormValues) => notaEntregue(nota) && eNotaPallet(nota);
export const motivoDevolucaoEClienteAusente = (motivo: string) => String(motivo) == "4";

export function novoFormularioEntregaGrupo(entrega: Entrega): EntregaGrupoFormValues {
  return {
    notas: entrega.notas.map((nota: CargaItem) => ({
      j10_ukey: nota.j10_ukey,
      nota_numero: nota.nota_numero,
      e_nota_pallet: nota.e_nota_pallet || "0",
      recusou: false,
      url_imagem1: "",
      url_imagem2: "",
      motivo_devolucao: "1",
      motivo_devolucao_descricao: "",
      e_tentativa_entrega: 0,
      status_nota_pallet_gestao_entrega: 1,
      motivo_nao_retorno_do_vale_pallet: "",
      url_imagem_vale_pallet: "",
    })),
    nome_recebedor: "",
    url_assinatura_recebedor: "",
    observacoes: "",
    teve_devolucao_mercadoria: null,
    url_imagem_devolucao_mercadoria: "",
  };
}

type ErrosNota = FormikErrors<NotaEntregaFormValues>;

function validaNotaEntregue(nota: NotaEntregaFormValues, regras: RegistroEntregaRegrasEfetivas): ErrosNota {
  const erros: ErrosNota = {};
  if (regras.fotoCanhotoObrigatoria && !nota.url_imagem1) {
    erros.url_imagem1 = "Tire a foto do canhoto!";
  }
  return erros;
}

function validaNomeRecebedor(nome_recebedor: string, regras: RegistroEntregaRegrasEfetivas): string | null {
  if (!regras.nomeRecebedorObrigatorio) return null;
  const nome = String(nome_recebedor || "").trim();
  if (!nome) return "Informe o nome do recebedor.";
  if (!nomeRecebedorTemNomeESobrenome(nome)) return "Informe nome e sobrenome do recebedor (nome completo).";
  return null;
}

function validaNotaRecusada(
  nota: NotaEntregaFormValues,
  devolucaoExigirApenasFoto2: boolean,
  motivoTextoLivre: boolean
): ErrosNota {
  const erros: ErrosNota = {};
  const erroMotivo = erroDoMotivoDevolucao(nota, motivoTextoLivre);
  if (erroMotivo) {
    if (motivoTextoLivre) {
      erros.motivo_devolucao_descricao = erroMotivo;
    } else {
      erros.motivo_devolucao = erroMotivo;
    }
  }
  if (devolucaoExigirApenasFoto2) {
    if (!nota.url_imagem2) erros.url_imagem2 = "Tire a foto 2!";
  } else {
    if (!nota.url_imagem1) erros.url_imagem1 = "Tire a foto do canhoto!";
  }
  return erros;
}

function validaPallet(nota: NotaEntregaFormValues): ErrosNota {
  const erros: ErrosNota = {};
  if (nota.status_nota_pallet_gestao_entrega == 1 && !nota.motivo_nao_retorno_do_vale_pallet) {
    erros.motivo_nao_retorno_do_vale_pallet = "Explique por que não vai ter o retorno";
  }
  if (nota.status_nota_pallet_gestao_entrega == 3 && !nota.url_imagem_vale_pallet) {
    erros.url_imagem_vale_pallet = "Adicione a foto do vale pallet";
  }
  return erros;
}

export function validarFormularioEntregaGrupo(
  form: EntregaGrupoFormValues,
  regras: RegistroEntregaRegrasEfetivas,
  opts: OpcoesValidacaoEntregaGrupo
): FormikErrors<EntregaGrupoFormValues> | null {
  const erros: FormikErrors<EntregaGrupoFormValues> = {};
  const errosNotas: (ErrosNota | undefined)[] = [];
  let temErroNota = false;

  form.notas.forEach((nota, indice) => {
    let errosDaNota: ErrosNota = nota.recusou
      ? validaNotaRecusada(nota, opts.devolucaoExigirApenasFoto2, !!opts.motivoTextoLivre)
      : validaNotaEntregue(nota, regras);

    if (opts.modalJaAberto && notaEntreguePallet(nota)) {
      errosDaNota = { ...errosDaNota, ...validaPallet(nota) };
    }

    if (Object.keys(errosDaNota).length) {
      errosNotas[indice] = errosDaNota;
      temErroNota = true;
    }
  });

  if (temErroNota) {
    erros.notas = errosNotas as FormikErrors<EntregaGrupoFormValues>["notas"];
  }

  const algumaEntregue = form.notas.some(notaEntregue);
  const erroNome = algumaEntregue ? validaNomeRecebedor(form.nome_recebedor, regras) : null;
  if (erroNome) {
    erros.nome_recebedor = erroNome;
  }
  if (regras.assinaturaRecebedorObrigatoria && algumaEntregue && !String(form.url_assinatura_recebedor || "").trim()) {
    erros.url_assinatura_recebedor = "Colete a assinatura do recebedor.";
  }

  if (opts.modalJaAberto && form.notas.some(notaEntregueNaoPallet)) {
    if (form.teve_devolucao_mercadoria === null || form.teve_devolucao_mercadoria === undefined) {
      erros.teve_devolucao_mercadoria = "Selecione uma opção";
    } else if (form.teve_devolucao_mercadoria == 1) {
      if (!form.observacoes) erros.observacoes = "Preencha o motivo da devolução.";
      if (!form.url_imagem_devolucao_mercadoria) erros.url_imagem_devolucao_mercadoria = "Tire a foto da Nota Fiscal";
    }
  }

  return Object.keys(erros).length ? erros : null;
}

export function montarGestoesDaEntrega(
  base: GestaoEntrega,
  form: EntregaGrupoFormValues,
  extras: ExtrasEntregaGrupo
): GestaoEntrega[] {
  let questionarioJaAnexado = false;

  return form.notas.map((nota): GestaoEntrega => {
    const comum: GestaoEntrega = {
      ...base,
      j81_ukeyp: nota.j10_ukey,
      j81_par: "J10",
      latitude: extras.latitude,
      longitude: extras.longitude,
      nome_recebedor: form.nome_recebedor,
      url_imagem1: nota.url_imagem1,
      url_imagem2: nota.url_imagem2,
      observacoes: form.observacoes,
      respostas_questionario: [],
    };

    if (nota.recusou) {
      const motivoEmTexto = motivoDevolucaoEmTexto(nota.motivo_devolucao_descricao);
      // Com o motivo em texto livre não existe o código 4 para reconhecer "cliente ausente":
      // a escolha de reentrega do motorista vale para qualquer motivo descrito.
      const perguntouReentrega =
        motivoDevolucaoEClienteAusente(nota.motivo_devolucao) || temMotivoDevolucaoEmTexto(motivoEmTexto);

      return {
        ...comum,
        motivo_devolucao: nota.motivo_devolucao,
        motivo_devolucao_descricao: motivoEmTexto,
        e_tentativa_entrega: perguntouReentrega ? Number(nota.e_tentativa_entrega) || 0 : 0,
        url_assinatura_recebedor: "",
        teve_devolucao_mercadoria: null as any,
      };
    }

    const respostas = questionarioJaAnexado ? [] : extras.respostas_questionario;
    questionarioJaAnexado = true;

    const entregue: GestaoEntrega = {
      ...comum,
      motivo_devolucao: "1",
      motivo_devolucao_descricao: "",
      e_tentativa_entrega: 0,
      url_assinatura_recebedor: form.url_assinatura_recebedor,
      respostas_questionario: respostas,
    };

    if (eNotaPallet(nota)) {
      return {
        ...entregue,
        status_nota_pallet_gestao_entrega: nota.status_nota_pallet_gestao_entrega,
        motivo_nao_retorno_do_vale_pallet: nota.motivo_nao_retorno_do_vale_pallet,
        url_imagem_vale_pallet: nota.url_imagem_vale_pallet,
        teve_devolucao_mercadoria: null as any,
      };
    }

    const teveDevolucao = form.teve_devolucao_mercadoria == 1;
    return {
      ...entregue,
      teve_devolucao_mercadoria: form.teve_devolucao_mercadoria as any,
      url_imagem2: nota.url_imagem2 || (teveDevolucao ? form.url_imagem_devolucao_mercadoria : ""),
    };
  });
}

export function montarGestoesDevolucaoTotal(
  form: GestaoEntrega,
  notas: CargaItem[],
  coords: { latitude: number; longitude: number }
): GestaoEntrega[] {
  return notas.map((nota) => ({
    ...form,
    j81_ukeyp: nota.j10_ukey,
    j81_par: "J10",
    latitude: coords.latitude,
    longitude: coords.longitude,
  }));
}
